import { Controller } from "@hotwired/stimulus"
import { showNotification } from "../helpers"

// Kontroler modali "Przypomnij o komplecie" (pojedyncze + masowe).
// Modal (backdrop/zamknięcie/panel) renderowany przez Tailwind::ModalComponent jako element
// potomny tego kontrolera — open/close szuka [data-controller~="modal"] wewnątrz siebie.
// Otwarcie: window event → setup/setupBulk → dispatch modal:open na elemencie modala.
// Zamknięcie: dispatch modal:close → modal controller zamyka.
// Wartość domyślna inputa dnia w modalu (_send_reminder_modal). Input jest poza formularzem,
// więc nic nie blokuje wyczyszczenia go — pusta wartość przy włączonym auto-przypomnieniu
// zostałaby zapisana jako NULL, którego zapytanie crona nigdy nie dopasuje.
const DEFAULT_AUTO_REMINDER_DAY = 10

export default class extends Controller {
  static targets = [
    "subtitle", "email", "body", "autoEnabled", "autoDay", "saveAsDefault", "sendButton", "saveButton",
    "bulkSubtitle", "bulkBody", "bulkSendButton"
  ]

  static values = {
    accountId: String,
    month: String,
    sendReminderUrl: String,
    autoReminderUrl: String,
    templateUrl: String,
    bulkUrl: String,
    bulkAccountIds: Array,
    translations: Object
  }

  // --- Pojedyncze przypomnienie ---

  setup({ detail: { accountId, companyName, email, month, monthDisplay, autoEnabled, autoDay, triggerElement } }) {
    this.accountIdValue = String(accountId)
    this.monthValue = String(month)
    this.triggerElement = triggerElement

    this.subtitleTarget.textContent = `${companyName} · ${monthDisplay}`
    this.emailTarget.textContent = email

    const isAutoEnabled = String(autoEnabled) === "true"
    this.autoEnabledTarget.checked = isAutoEnabled
    this.autoDayTarget.value = autoDay || DEFAULT_AUTO_REMINDER_DAY
    this.autoDayTarget.disabled = !isAutoEnabled

    this.openModal()
  }

  toggleAutoDay() {
    this.autoDayTarget.disabled = !this.autoEnabledTarget.checked
    if (!this.autoDayTarget.disabled) this.autoDayTarget.focus()
  }

  async send() {
    await this.withBusyButton(this.sendButtonTarget, "sending", "send_label", async () => {
      const response = await this.postJson(this.sendReminderUrlValue, {
        account_id: this.accountIdValue, month: this.monthValue, body: this.bodyTarget.value
      })
      if (response.ok) {
        this.closeModal()
        showNotification(this.translationsValue.sent)
      } else {
        showNotification(this.translationsValue.error)
      }
    })
  }

  async save() {
    const autoEnabled = this.autoEnabledTarget.checked
    const autoDay = autoEnabled ? (this.autoDayTarget.value.trim() || DEFAULT_AUTO_REMINDER_DAY) : null
    const saveAsDefault = this.hasSaveAsDefaultTarget && this.saveAsDefaultTarget.checked

    await this.withBusyButton(this.saveButtonTarget, "saving", "save_label", async () => {
      const requests = [
        this.postJson(this.autoReminderUrlValue,
          { account_id: this.accountIdValue, enabled: autoEnabled, day: autoDay }, "PATCH")
      ]
      if (saveAsDefault) {
        requests.push(this.postJson(this.templateUrlValue, { body: this.bodyTarget.value }, "PATCH"))
      }

      const responses = await Promise.all(requests)
      const allOk = responses.every(r => r.ok)
      if (allOk && this.triggerElement) {
        this.triggerElement.setAttribute("data-accounting-office--completeness-reminder-trigger-auto-enabled-param", String(autoEnabled))
        this.triggerElement.setAttribute("data-accounting-office--completeness-reminder-trigger-auto-day-param", String(autoDay || DEFAULT_AUTO_REMINDER_DAY))
      }
      showNotification(allOk ? this.translationsValue.settings_saved : this.translationsValue.error)
    })
  }

  // --- Masowe przypomnienie ---

  setupBulk({ detail: { month, selectedIds } }) {
    this.monthValue = String(month)
    this.bulkAccountIdsValue = selectedIds

    const count = selectedIds.length
    const template = count === 1 ? this.translationsValue.bulk_subtitle_one : this.translationsValue.bulk_subtitle_other
    this.bulkSubtitleTarget.textContent = template.replace("%{n}", count)

    this.openModal()
  }

  async sendBulk() {
    await this.withBusyButton(this.bulkSendButtonTarget, "sending", "send_label", async () => {
      const response = await this.postJson(this.bulkUrlValue, {
        account_ids: this.bulkAccountIdsValue, month: this.monthValue, body: this.bulkBodyTarget.value
      })
      if (!response.ok) {
        showNotification(this.translationsValue.error)
        return
      }
      const data = await response.json()
      this.closeModal()
      showNotification(this.translationsValue.bulk_result_template.replace("%{sent}", data.sent).replace("%{skipped}", data.skipped))
    })
  }

  // --- Private ---

  // Wspólny szkielet akcji: blokada przycisku na czas requestu + notyfikacja błędu sieci
  async withBusyButton(btn, busyKey, idleKey, fn) {
    btn.disabled = true
    btn.textContent = this.translationsValue[busyKey]
    try {
      await fn()
    } catch {
      showNotification(this.translationsValue.error)
    } finally {
      btn.disabled = false
      btn.textContent = this.translationsValue[idleKey]
    }
  }

  postJson(url, payload, method = "POST") {
    return fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
      },
      body: JSON.stringify(payload)
    })
  }

  modalElement() {
    return this.element.querySelector('[data-controller~="modal"]')
  }

  openModal() {
    this.modalElement().dispatchEvent(new CustomEvent("modal:open"))
  }

  closeModal() {
    this.modalElement().dispatchEvent(new CustomEvent("modal:close"))
  }
}
